import {AxiosError, AxiosInstance, isAxiosError, ResponseType} from 'axios';
import store from '@/store';
import {LoginApi} from './login.api.class';
import router from '@/router';
import {ExceptionsCollector} from '@/exceptions/ExceptionsCollector';
import {ClientHooks} from '@/hooks';
import {rememberExpiredSession} from '@/helpers/sessionEnd.helper';

export type ApiRequestOptions = {
    /**
     * HTTP status codes the caller handles itself (e.g. 409 Conflict): the error is re-thrown unchanged
     * without the global error handling (error dialog, logout, …).
     */
    handledStatuses?: number[];
};

/**
 * Loads of a single card / section (e.g. "Nächste Rechnung" in the billing tab): the card shows the error inline
 * (with retry), the global error dialog must not pop up on top of the page.
 */
export const CARD_LOAD_OPTIONS: ApiRequestOptions = {handledStatuses: [400, 403, 404, 422, 500, 502, 503, 504]};

export abstract class API {
    protected static service: string;
    /** Set once the expired session is being logged out (until the reload) */
    private static sessionEnding = false;

    protected static isHandledByCaller(error: unknown, options?: ApiRequestOptions): boolean {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        return !!status && !!options?.handledStatuses?.includes(status);
    }

    protected static async post(path: string, body: any, responseType?: ResponseType, options?: ApiRequestOptions) {
        if (!store.state.axios) {
            return;
        }
        try {
            return (await (store.state.axios as AxiosInstance).post(`${this.service}/${path}`, body, {
                responseType,
            })).data;
        } catch (error) {
            if (API.isHandledByCaller(error, options)) {
                throw error;
            }
            const vanillaException = ExceptionsCollector.tryCast(await API.unwrapBlobError(error));
            if (vanillaException) {
                throw vanillaException;
            }
            this.handleAxiosError(error as AxiosError);
            throw error;
        }
    }

    /**
     * Requests with responseType "blob" (file downloads) also get the error body as Blob.
     * A JSON error body is read as text so it can be cast to the VanillaException the server threw.
     */
    protected static async unwrapBlobError(error: unknown): Promise<unknown> {
        const data = isAxiosError(error) ? error.response?.data : undefined;
        if (typeof Blob === 'undefined' || !(data instanceof Blob) || !data.type.includes('json')) {
            return error;
        }
        try {
            return await data.text();
        } catch (e) {
            return error;
        }
    }

    protected static async get(path: string, params?: any, responseType?: ResponseType, options?: ApiRequestOptions) {
        if (!store.state.axios) {
            return;
        }
        try {
            if (params) {
                return (await (store.state.axios as AxiosInstance).get(`${this.service}/${path}`, {
                    params: {
                        json: JSON.stringify(params),
                    },
                    responseType,
                })).data;
            } else {
                return (await (store.state.axios as AxiosInstance).get(`${this.service}/${path}`)).data;
            }
        } catch (error) {
            if (API.isHandledByCaller(error, options)) {
                throw error;
            }
            const vanillaException = ExceptionsCollector.tryCast(await API.unwrapBlobError(error));
            if (vanillaException) {
                throw vanillaException;
            }
            this.handleAxiosError(error as AxiosError);
            throw error;
        }
    }

    protected static async put(path: string, body: any, options?: ApiRequestOptions) {
        if (!store.state.axios) {
            return;
        }
        try {
            return (await (store.state.axios as AxiosInstance).put(`${this.service}/${path}`, body)).data;
        } catch (error) {
            if (API.isHandledByCaller(error, options)) {
                throw error;
            }
            const vanillaException = ExceptionsCollector.tryCast(error);
            if (vanillaException) {
                throw vanillaException;
            }
            this.handleAxiosError(error as AxiosError);
            throw error;
        }
    }

    protected static async patch(path: string, body: any, options?: ApiRequestOptions) {
        if (!store.state.axios) {
            return;
        }
        try {
            return (await (store.state.axios as AxiosInstance).patch(`${this.service}/${path}`, body)).data;
        } catch (error) {
            if (API.isHandledByCaller(error, options)) {
                throw error;
            }
            const vanillaException = ExceptionsCollector.tryCast(error);
            if (vanillaException) {
                throw vanillaException;
            }
            this.handleAxiosError(error as AxiosError);
            throw error;
        }
    }

    protected static async delete(path: string, params?: any, options?: ApiRequestOptions) {
        if (!store.state.axios) {
            return;
        }
        try {
            if (params) {
                let _pass: string | undefined;
                if (params._pass) {
                    _pass = params._pass;
                    delete params._pass;
                }
                return (await (store.state.axios as AxiosInstance).delete(`${this.service}/${path}`, {
                    params: {
                        json: JSON.stringify(params),
                    },
                    data: _pass ? {_pass} : undefined,
                })).data;
            } else {
                return (await (store.state.axios as AxiosInstance).delete(`${this.service}/${path}`)).data;
            }
        } catch (error) {
            if (API.isHandledByCaller(error, options)) {
                throw error;
            }
            const vanillaException = ExceptionsCollector.tryCast(error);
            if (vanillaException) {
                throw vanillaException;
            }
            this.handleAxiosError(error as AxiosError);
            throw error;
        }
    }

    protected static async handleAxiosError(axiosError: AxiosError) {
        switch (axiosError.response?.status) {
            case 401:
                // Several requests fail at once when the session expires: log out only once
                if (API.sessionEnding) {
                    break;
                }
                API.sessionEnding = true;
                // The login shows a hint and leads back to this page (survives the reload)
                rememberExpiredSession(router.currentRoute.fullPath);
                try {
                    await ClientHooks.preLogout();
                } catch (e) {
                    // the reload follows directly, a snackbar would not be visible
                }
                await LoginApi.logout({sessionExpired: true});
                try {
                    await ClientHooks.postLogout();
                } catch (e) {
                    // see above
                }
                LoginApi.reloadToLogin();
                break;
            case 403:
                store.commit('showUnauthorizedDialog', true);
                break;
            case 404:
                break;
            case 420:
                break;
            case 422:
                store.commit('showSnackbar', {text: 'Die übermittelten Daten waren fehlerhaft', btnColor: 'error'});
                break;
            case 429:
                store.commit('showSnackbar', {text: 'Das Anfragelimit wurde überschritten', btnColor: 'error'});
                break;
            case 500:
                if (!axiosError.config?.url?.includes('finder')) {
                    store.commit('setError', axiosError);
                }
                break;
            case 502:
                store.commit('showSnackbar', {
                    text: 'Die Verbindung zum externen Dienstleister konnte nicht hergestellt werden',
                    btnColor: 'error',
                });
                break;
            default:
                store.commit('setError', axiosError);
                break;
        }
    }
}
